import { AlertToastType, ToastController } from '@boomi/exosphere';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { JwtToken } from '../types/global';
import { AppBody } from './AppBody';
import { useUrlSearchParams } from './hooks/useUrlSearchParams';
import type { ApprovalTaskStatusWithAny, DetailTask, InboxTasksRequest, ListTask } from './sources';
import {
    approveTask as approveTaskApi,
    getApprovalTask,
    getTasks,
    denyTask as rejectTaskApi,
} from './sources';
import { tryGetErrorMessage } from './utils';
import { navigateToNextTask } from './utils/navigationUtils';
import { initialState, taskReducer } from './utils/taskReducer';

export type TabType = 'assigned' | 'watching';

export interface TaskDataManagerData {
    // inbox tasks
    inboxTasks: ListTask[];
    isLoadingInbox: boolean;
    loadInboxTasks: (request: InboxTasksRequest) => Promise<void>;
    getNextInboxTasksPage: () => Promise<void>;
    isMoreToLoad: boolean;
    totalCount: number | undefined;
    // selected task
    selectedTask: DetailTask | null;
    isLoadingSelectedTask: boolean;
    loadSelectedTask: (id: string | null) => void;
    selectedTaskId?: string | undefined;
    // approve/reject
    approveTask: (id: string, comments: string | null) => Promise<void>;
    rejectTask: (id: string, comments: string | null) => Promise<void>;
    // URL-based filter state
    currentTab: TabType;
    currentStatus: ApprovalTaskStatusWithAny;
    updateFilters: (tab?: TabType, status?: ApprovalTaskStatusWithAny) => void;
}

const addError = (error: string) =>
    ToastController.show({
        description: error,
        type: AlertToastType.ERROR,
    });

// Query parameter keys for filters
const QUERY_PARAMS = {
    TAB: 'tab',
    STATUS: 'status',
} as const;

export const TaskDataManager = ({ jwt }: { jwt: JwtToken }) => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { getParam, updateParams, searchParams } = useUrlSearchParams();

    const [state, dispatch] = useReducer(taskReducer, {
        ...initialState,
        currentUserEmail: jwt.username,
    });
    const connection = useRef<signalR.HubConnection | null>(null);
    const hasSetupSignalR = useRef<boolean>(false);
    const animationTimeouts = useRef<Map<string, number>>(new Map());

    // Helper functions for case-insensitive URL parameter parsing
    const parseTabFromUrl = (value: string | null): TabType => {
        if (!value) return 'assigned';
        const normalized = value.toLowerCase();
        return normalized === 'watching' ? 'watching' : 'assigned';
    };

    const parseStatusFromUrl = (
        value: string | null,
        currentTab: TabType,
    ): ApprovalTaskStatusWithAny => {
        if (currentTab === 'assigned' && !value) return 'Pending'; // Default for assigned tab

        if (!value) return 'Any';
        const normalized = value.toLowerCase();

        // Map lowercase values to proper case
        const statusMap: Record<string, ApprovalTaskStatusWithAny> = {
            pending: 'Pending',
            approved: 'Approved',
            denied: 'Denied',
            expired: 'Expired',
            canceled: 'Canceled',
            any: 'Any',
        };

        return statusMap[normalized] || 'Any';
    };

    const currentTab = parseTabFromUrl(getParam(QUERY_PARAMS.TAB));
    const currentStatus = parseStatusFromUrl(getParam(QUERY_PARAMS.STATUS), currentTab);
    const currentTabRef = useRef<TabType>(currentTab);

    // Helper functions to convert values to lowercase for URL consistency
    const tabToUrlValue = (tab: TabType): string | null => {
        return tab === 'assigned' ? null : tab.toLowerCase();
    };

    const statusToUrlValue = (status: ApprovalTaskStatusWithAny): string | null => {
        return status === 'Any' ? null : status.toLowerCase();
    };

    // Function to update filter state in URL
    const updateFilters = (tab?: TabType, status?: ApprovalTaskStatusWithAny) => {
        const updates: Record<string, string | null> = {};

        if (tab !== undefined) {
            updates[QUERY_PARAMS.TAB] = tabToUrlValue(tab);
        }

        if (status !== undefined) {
            updates[QUERY_PARAMS.STATUS] = statusToUrlValue(status);
        }

        updateParams(updates);
    };

    // Keep currentTabRef updated with the latest currentTab value
    // otherwise it will need to be a dependency of the setupSignalR useEffect
    useEffect(() => {
        currentTabRef.current = currentTab;
    }, [currentTab]);

    // Setup SignalR connection
    useEffect(() => {
        const setupSignalR = async () => {
            try {
                connection.current = new HubConnectionBuilder()
                    .withUrl('/realtime/inbox', {
                        skipNegotiation: true,
                        transport: HttpTransportType.WebSockets,
                        accessTokenFactory: () => jwt.token,
                    })
                    .withAutomaticReconnect()
                    .configureLogging(LogLevel.Information)
                    .build();

                connection.current.on('TaskAdded', (newTask: ListTask) => {
                    dispatch({
                        type: 'REALTIME_ADD_TASK',
                        task: newTask,
                        currentTab: currentTabRef.current,
                    });
                });

                connection.current.on('TaskRemoved', (removedTask: ListTask) => {
                    dispatch({
                        type: 'REALTIME_REMOVE_TASK',
                        taskId: removedTask.id,
                    });
                });

                await connection.current.start();
                hasSetupSignalR.current = true;
            } catch (error) {
                console.error('SignalR: Failed to setup connection', error);
            }
        };

        // Only setup SignalR if JWT is present and we haven't set up SignalR yet
        if (jwt && !hasSetupSignalR.current) {
            setupSignalR();
        }

        // Cleanup function to stop connection when JWT changes or component unmounts
        return () => {
            if (connection.current) {
                connection.current.stop().catch((error) => {
                    console.error('SignalR: Error during disconnect', error);
                });
                hasSetupSignalR.current = false;
            }
        };
    }, [jwt]);

    // Handle animation timing for newly added tasks
    useEffect(() => {
        if (!state.inboxTasks) return;

        state.inboxTasks.forEach((task) => {
            if (task.isNewlyAdded && !animationTimeouts.current.has(task.id)) {
                const timeoutId = window.setTimeout(() => {
                    dispatch({ type: 'CLEAR_NEWLY_ADDED_FLAG', taskId: task.id });
                    animationTimeouts.current.delete(task.id);
                }, 500); // Match entrance animation duration

                animationTimeouts.current.set(task.id, timeoutId);
            }

            if (task.isRemoving && !animationTimeouts.current.has(`remove_${task.id}`)) {
                const timeoutId = window.setTimeout(() => {
                    dispatch({ type: 'COMPLETE_REMOVE_ANIMATION', taskId: task.id });
                    animationTimeouts.current.delete(`remove_${task.id}`);
                }, 500); // Match exit animation duration

                animationTimeouts.current.set(`remove_${task.id}`, timeoutId);
            }
        });

        return () => {
            // Clean up timeouts on unmount
            animationTimeouts.current.forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
            animationTimeouts.current.clear();
        };
    }, [state.inboxTasks]);

    // Load inbox data based on current URL params
    // biome-ignore lint/correctness/useExhaustiveDependencies: jwt cannot be a dependency because it gets refreshed every few minutes. If the username hasn't changed we can trust the jwt.
    useEffect(() => {
        const loadDataForCurrentFilters = async () => {
            if (!jwt) return;

            dispatch({ type: 'SET_LOADING_INBOX', isLoading: true });

            try {
                const request: InboxTasksRequest = {
                    status: currentStatus,
                    isWatchingTasks: currentTab === 'watching',
                };

                const { tasks, ...paging } = await getTasks(request, jwt);

                dispatch({
                    type: 'SET_INBOX_TASKS',
                    tasks,
                    request: {
                        ...request,
                        page: paging.page,
                        pageSize: paging.pageSize,
                        total: paging.total,
                    },
                });
            } catch (err) {
                addError(tryGetErrorMessage(err, 'Failed to load inbox tasks'));
            } finally {
                dispatch({ type: 'SET_LOADING_INBOX', isLoading: false });
            }
        };

        // Load data when JWT is available or when filters change
        loadDataForCurrentFilters();
    }, [jwt?.username, currentTab, currentStatus]);

    // Handle task selection based on URL (both initial and subsequent changes)
    // biome-ignore lint/correctness/useExhaustiveDependencies: jwt cannot be a dependency because it gets refreshed every few minutes. If the username hasn't changed we can trust the jwt.
    useEffect(() => {
        const loadTaskFromUrl = async () => {
            if (taskId && taskId !== state.selectedTask?.id) {
                dispatch({ type: 'SET_LOADING_SELECTED_TASK', isLoading: true });
                try {
                    const task = await getApprovalTask(taskId, jwt);
                    dispatch({ type: 'SET_SELECTED_TASK', task });
                } catch (err) {
                    const message = `Failed to load task with ID: ${taskId}`;
                    console.error(tryGetErrorMessage(err, message));
                    addError(message);
                    // Navigate to home if task doesn't exist
                    navigate('/', { replace: true });
                } finally {
                    dispatch({ type: 'SET_LOADING_SELECTED_TASK', isLoading: false });
                }
            } else if (!taskId && state.selectedTask) {
                dispatch({ type: 'SET_SELECTED_TASK', task: null });
            }
        };

        // Only load task if we have a JWT
        if (jwt) {
            loadTaskFromUrl();
        }
    }, [taskId, jwt?.username, navigate, state.selectedTask]);

    const loadInboxTasks = useCallback(
        async (request: InboxTasksRequest) => {
            dispatch({ type: 'SET_LOADING_INBOX', isLoading: true });

            try {
                const { tasks, ...paging } = await getTasks(
                    { ...request, isWatchingTasks: currentTab === 'watching' },
                    jwt,
                );

                dispatch({
                    type: paging.page === 1 ? 'SET_INBOX_TASKS' : 'APPEND_INBOX_TASKS',
                    tasks,
                    request: {
                        ...request,
                        page: paging.page,
                        pageSize: paging.pageSize,
                        total: paging.total,
                    },
                });
            } catch (err) {
                addError(tryGetErrorMessage(err, 'Failed to load inbox tasks'));
            } finally {
                dispatch({ type: 'SET_LOADING_INBOX', isLoading: false });
            }
        },
        [jwt, currentTab],
    );

    const getNextInboxTasksPage = useCallback(
        async () =>
            await loadInboxTasks({
                ...state.currentRequest,
                page: (state.currentRequest.page ?? 0) + 1,
                status: currentStatus,
            }),
        [state.currentRequest, loadInboxTasks, currentStatus],
    );

    const loadSelectedTask = (id: string | null) => {
        const queryString = searchParams.toString();
        if (id) {
            const url = `/task/${id}${queryString ? `?${queryString}` : ''}`;
            navigate(url);
        } else {
            const url = `/${queryString ? `?${queryString}` : ''}`;
            navigate(url);
            dispatch({ type: 'SET_SELECTED_TASK', task: null });
        }
    };

    const approveTask = async (id: string, comments: string | null) => {
        try {
            await approveTaskApi(id, comments, jwt);
            navigateToNextTask(navigate, state.inboxTasks, id);
            ToastController.show({
                description: 'Approved successfully',
                type: AlertToastType.SUCCESS,
            });
        } catch (err) {
            addError(tryGetErrorMessage(err, 'Failed to approve task'));
        }
    };

    const rejectTask = async (id: string, comments: string | null) => {
        try {
            await rejectTaskApi(id, comments, jwt);
            navigateToNextTask(navigate, state.inboxTasks, id);
            ToastController.show({
                description: 'Denied successfully',
                type: AlertToastType.SUCCESS,
            });
        } catch (err) {
            addError(tryGetErrorMessage(err, 'Failed to reject task'));
        }
    };

    const { total, page, pageSize } = state.currentRequest;
    const isMoreToLoad =
        total !== undefined &&
        page !== undefined &&
        pageSize !== undefined &&
        page * pageSize < total;

    return (
        <AppBody
            // inbox tasks
            inboxTasks={state.inboxTasks}
            isLoadingInbox={state.isLoadingInbox}
            loadInboxTasks={loadInboxTasks}
            getNextInboxTasksPage={getNextInboxTasksPage}
            isMoreToLoad={isMoreToLoad}
            totalCount={total}
            // selectedTask
            selectedTask={state.selectedTask}
            isLoadingSelectedTask={state.isLoadingSelectedTask}
            loadSelectedTask={loadSelectedTask}
            selectedTaskId={taskId}
            // approve/reject
            approveTask={approveTask}
            rejectTask={rejectTask}
            // URL-based filter state
            currentTab={currentTab}
            currentStatus={currentStatus}
            updateFilters={updateFilters}
        />
    );
};

export default TaskDataManager;
